import { Injectable } from '@angular/core';
import {
  PunchoutBuildContext,
  PunchoutCredential,
  PunchoutCustomerConfig,
  PunchoutError,
  PunchoutOrderRow,
  PunchoutPayload,
  PunchoutSetupCredentials
} from '../../../models/punchout';
import { PunchoutProtocolHandler } from './punchout-protocol.handler';

@Injectable({ providedIn: 'root' })
export class CxmlPunchoutHandler extends PunchoutProtocolHandler {
  readonly protocol: 'cxml' = 'cxml';
  readonly identifier: string = 'cxml-default';
  readonly label: string = 'cXML (default)';

  buildPayload(rows: PunchoutOrderRow[], context?: PunchoutBuildContext): PunchoutPayload {
    const buyerCookie = context?.session?.buyerCookie;
    if (!buyerCookie) {
      throw new PunchoutError(
        'PUNCHOUT_CXML_BUYER_COOKIE_MISSING',
        'cXML punchout return requires the original buyerCookie.'
      );
    }

    if (!rows.length) {
      throw new PunchoutError('PUNCHOUT_CXML_EMPTY_CART', 'cXML punchout return requires at least one cart row.');
    }

    const currency = this.getCurrency(rows);
    const total = rows.reduce((sum, row) => sum + this.getLineTotal(row), 0);
    const config = context?.currentConfig ?? null;
    const setupCredentials = context?.session?.setupCredentials;

    return {
      kind: 'cxml-form-post',
      xmlBody: this.buildXml(buyerCookie, rows, total, currency, config, setupCredentials)
    };
  }

  protected buildXml(
    buyerCookie: string,
    rows: PunchoutOrderRow[],
    total: number,
    currency: string,
    config: PunchoutCustomerConfig | null,
    setupCredentials?: PunchoutSetupCredentials
  ): string {
    const timestamp = new Date().toISOString();
    const payloadId = `${Date.now()}.${Math.random().toString(36).slice(2)}@gung`;

    // The returning PunchOutOrderMessage echoes the setup request with From/To swapped:
    //   setup.to   (supplier, us)    → response.From   + response.Sender Identity
    //   setup.from (buyer's system)  → response.To
    // Buyer systems (Ariba, Coupa, Oracle iProcurement, …) match the response against
    // the setup request by Credential Identity+domain; using their values is the only
    // reliable way to be accepted. Customer-config `extra.*` overrides still win when
    // explicitly set; otherwise we use echoed creds, finally falling back to defaults.
    const supplierCred = setupCredentials?.to;
    const buyerCred = setupCredentials?.from;

    const fromDomain = this.resolveDomain(config, 'fromDomain', supplierCred);
    const fromIdentity = this.resolveIdentity(config, 'fromIdentity', supplierCred, 'Gung');
    const toDomain = this.resolveDomain(config, 'toDomain', buyerCred);
    const toIdentity = this.resolveIdentity(config, 'toIdentity', buyerCred, config?.name || 'buyer');
    const senderDomain = this.resolveDomain(config, 'senderDomain', supplierCred);
    const senderIdentity = this.resolveIdentity(config, 'senderIdentity', supplierCred, 'Gung');
    const userAgent = this.getConfigString(config, 'userAgent', 'Gung B2B PunchOut');
    const operationAllowed = this.getConfigString(config, 'operationAllowed', 'create');

    return [
      '<?xml version="1.0" encoding="UTF-8"?>',
      '<!DOCTYPE cXML SYSTEM "http://xml.cxml.org/schemas/cXML/1.2.014/cXML.dtd">',
      `<cXML payloadID="${this.escapeAttribute(payloadId)}" timestamp="${this.escapeAttribute(timestamp)}">`,
      '<Header>',
      this.credential('From', fromDomain, fromIdentity),
      this.credential('To', toDomain, toIdentity),
      '<Sender>',
      this.credentialContent(senderDomain, senderIdentity),
      `<UserAgent>${this.escapeText(userAgent)}</UserAgent>`,
      '</Sender>',
      '</Header>',
      '<Message>',
      '<PunchOutOrderMessage>',
      `<BuyerCookie>${this.escapeText(buyerCookie)}</BuyerCookie>`,
      `<PunchOutOrderMessageHeader operationAllowed="${this.escapeAttribute(operationAllowed)}">`,
      `<Total><Money currency="${this.escapeAttribute(currency)}">${this.formatMoney(total, 2)}</Money></Total>`,
      '</PunchOutOrderMessageHeader>',
      rows.map((row, index) => this.itemIn(row, index, currency, config)).join(''),
      '</PunchOutOrderMessage>',
      '</Message>',
      '</cXML>'
    ].join('');
  }

  protected itemIn(
    row: PunchoutOrderRow,
    index: number,
    currency: string,
    config: PunchoutCustomerConfig | null
  ): string {
    const description = row.product?.name || row.product?.description || row.productId;
    const classificationDomain = this.getConfigString(config, 'classificationDomain', 'UNSPSC');
    const classification = this.resolveClassification(row, config);
    const language = this.getConfigString(config, 'language', 'en');

    return [
      `<ItemIn quantity="${this.escapeAttribute(this.formatQuantity(row.quantity))}" lineNumber="${index + 1}">`,
      '<ItemID>',
      `<SupplierPartID>${this.escapeText(row.productId)}</SupplierPartID>`,
      '</ItemID>',
      '<ItemDetail>',
      `<UnitPrice><Money currency="${this.escapeAttribute(currency)}">${this.formatMoney(row.price, 6)}</Money></UnitPrice>`,
      `<Description xml:lang="${this.escapeAttribute(language)}">${this.escapeText(description)}</Description>`,
      '<UnitOfMeasure>EA</UnitOfMeasure>',
      `<Classification domain="${this.escapeAttribute(classificationDomain)}">${this.escapeText(classification)}</Classification>`,
      '</ItemDetail>',
      '</ItemIn>'
    ].join('');
  }

  protected resolveClassification(row: PunchoutOrderRow, config: PunchoutCustomerConfig | null): string {
    return this.getConfigString(config, 'classification', '00000000');
  }

  protected credential(type: string, domain: string, identity: string): string {
    return `<${type}>${this.credentialContent(domain, identity)}</${type}>`;
  }

  protected credentialContent(domain: string, identity: string): string {
    return [
      `<Credential domain="${this.escapeAttribute(domain)}">`,
      `<Identity>${this.escapeText(identity)}</Identity>`,
      '</Credential>'
    ].join('');
  }

  protected getCurrency(rows: PunchoutOrderRow[]): string {
    const currencies = Array.from(new Set(rows.map(row => row.currency).filter(Boolean)));
    if (currencies.length !== 1) {
      throw new PunchoutError(
        'PUNCHOUT_CXML_CURRENCY_MISSING',
        'cXML punchout return requires exactly one currency.'
      );
    }
    return currencies[0];
  }

  protected getLineTotal(row: PunchoutOrderRow): number {
    const quantity = row.quantity ?? 1;
    if (!Number.isFinite(quantity) || !Number.isFinite(row.price)) {
      throw new PunchoutError('PUNCHOUT_CXML_INVALID_ROW_TOTAL', 'cXML punchout row has invalid quantity or price.');
    }
    return quantity * row.price;
  }

  protected formatMoney(value: number, decimals: number): string {
    if (!Number.isFinite(value)) {
      throw new PunchoutError('PUNCHOUT_CXML_INVALID_MONEY', 'cXML punchout money value is invalid.');
    }
    return value.toFixed(decimals);
  }

  protected formatQuantity(value: number): string {
    if (!Number.isFinite(value)) {
      throw new PunchoutError('PUNCHOUT_CXML_INVALID_QUANTITY', 'cXML punchout quantity is invalid.');
    }
    return String(value);
  }

  protected getConfigString(config: PunchoutCustomerConfig | null, key: string, fallback: string): string {
    const value = config?.extra?.[key];
    return typeof value === 'string' && value ? value : fallback;
  }

  protected resolveDomain(
    config: PunchoutCustomerConfig | null,
    key: string,
    echoed: PunchoutCredential | undefined
  ): string {
    const override = config?.extra?.[key];
    if (typeof override === 'string' && override) {
      return override;
    }
    if (echoed?.domain) {
      return echoed.domain;
    }
    return 'NetworkID';
  }

  protected resolveIdentity(
    config: PunchoutCustomerConfig | null,
    key: string,
    echoed: PunchoutCredential | undefined,
    fallback: string
  ): string {
    const override = config?.extra?.[key];
    if (typeof override === 'string' && override) {
      return override;
    }
    if (echoed?.identity) {
      return echoed.identity;
    }
    return fallback;
  }

  protected escapeText(value: unknown): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  protected escapeAttribute(value: unknown): string {
    return this.escapeText(value).replace(/"/g, '&quot;').replace(/'/g, '&apos;');
  }
}
